import { Button, CloseButton } from '@ui/shared/Button';
import dayjs from 'dayjs';
import uniqBy from 'lodash/uniqBy';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { closePopup, openPopup } from '../../../data/providers/popupsProvider';
import { exportDateFormatSettingProvider } from '../../../data/providers/settings/exportDateFormatSettingProvider';
import { exportTests } from '../../../data/providers/testsProvider';
import {
  Entity,
  EntityType,
  IRange,
  ISelectOption,
  ITest,
  ITestType,
} from '../../../data/types';
import { saveExportDateFormatSetting } from '../../../data/writers/settings/exportDateFormatSetting';
import {
  ValidExportDateFormat,
  buildCSV,
  downloadZipFile,
  makeFilename,
} from '../../../utils/export';
import { CardBody } from '../../shared/card/CardBody';
import { CardFooter } from '../../shared/card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../shared/card/CardHeader';
import { ControlLabel } from '../../shared/form/ControlLabel';
import { ControlSpacer } from '../../shared/form/ControlSpacer';
import { ExportDateFormat } from '../../shared/form/ExportDateFormat';
import { FormContainer } from '../../shared/form/FormContainer';
import { Select } from '../../shared/form/Select';
import { SelectBox } from '../../shared/form/SelectBox';
import { LinearLoading } from '../../shared/loading/LinearLoading';
import { PopupCard, PopupCardContent } from '../../shared/popups/PopupCard';

interface IPopupProps {
  onClose: () => void;
  entity: Entity;
  entityType: EntityType;
  range: IRange;
  facet: string | null;
}

export const Popup = (props: IPopupProps): React.ReactElement => {
  const averageOptions = [
    { label: 'Tests', value: 'none' },
    { label: 'Averages', value: 'averagesOnly' },
    { label: 'Averages and Tests', value: 'averagesAnd' },
  ];

  const [loading, setLoading] = React.useState<boolean>(false);
  const [selectedOption, setSelectedOption] = React.useState(averageOptions[0]);
  const [isDisabled, setIsDisabled] = React.useState(false);
  const [includeInactiveMetrics, setIncludeInactiveMetrics] =
    React.useState<boolean>(false);
  const [deidentify, setDeidentify] = React.useState<boolean>(false);
  const [useDecimalComma, setUseDecimalComma] = React.useState<boolean>(false);
  const [dateFormat, setDateFormat] = React.useState<ValidExportDateFormat>();

  /**
   * @description subscribe to export date format setting
   */
  React.useEffect((): (() => void) => {
    const subscriber = exportDateFormatSettingProvider.subscribe(
      (value: ValidExportDateFormat): void => setDateFormat(value)
    );
    return (): void => subscriber.unsubscribe();
  }, []);

  const onExport = async (): Promise<void> => {
    exportTests({
      entityId: props.entity?.id,
      entityType: props.entityType,
      includeInactiveMetrics,
      range: props.range,
      deidentify,
      facet: props?.facet,
    })
      .toPromise()
      .then(async (tests: ITest[]): Promise<void> => {
        const entityName = deidentify ? props.entity.id : props.entity.name;
        const sessionDateStart = dayjs
          .utc(tests[tests.length - 1].sessionId)
          .format('MM_DD_YY');
        const sessionDateEnd = dayjs.utc(tests[0].sessionId).format('MM_DD_YY');
        const testTypes = tests.map((i: ITest): ITestType => i.testType);
        const data = uniqBy(testTypes, (t: ITestType): string => t.id).map(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (testType: ITestType): { filename: string; data: any } => {
            const testsOfType = tests.filter(
              (item: ITest): boolean => item.testType.id === testType.id
            );
            const mFilename = makeFilename(
              `${entityName}-${sessionDateStart}-${sessionDateEnd}-${
                selectedOption.value === 'none'
                  ? ''
                  : selectedOption.value === 'averagesOnly'
                  ? '-averagesOnly'
                  : '-averagesAndTests'
              }`,
              testType.name
            );
            return buildCSV(
              testsOfType,
              `${mFilename}.csv`,
              dateFormat,
              selectedOption.value,
              useDecimalComma
            );
          }
        );
        const filename = makeFilename(
          `${entityName}-${sessionDateStart}-${sessionDateEnd}${
            selectedOption.value === 'none'
              ? ''
              : selectedOption.value === 'averagesOnly'
              ? '-averagesOnly'
              : '-averagesAndTests'
          }.zip`
        );
        await downloadZipFile(data, filename);
        props.onClose();
      });
  };

  const download = async (): Promise<void> => {
    setLoading(true);
    setIsDisabled(true);
    setTimeout((): void => {
      onExport();
    }, 200);
  };

  const handleInactiveMetricsSelection = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setIncludeInactiveMetrics(e.currentTarget.checked);
  };

  /**
   * @description handles user deidentification selection
   * @param e
   * @returns
   */
  const handleDeidentificationOption = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => setDeidentify(e.currentTarget.checked);

  /**
   * @description handles user selecting use decimal comma in export
   */
  const handleUseDecimalComma = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setUseDecimalComma(e.currentTarget.checked);
  };

  /**
   * @description handles changes to export date format
   * @param value
   */
  const handleExportDateFormatSelection = (
    value: ValidExportDateFormat
  ): void => {
    saveExportDateFormatSetting(value);
  };

  return (
    <PopupCard onClose={props.onClose} size='medium' noClickOutside>
      <PopupCardContent>
        <CardHeader surface={8}>
          <CardHeaderLeft>
            <h6>Export Tests</h6>
          </CardHeaderLeft>
          <CardHeaderRight>
            <CloseButton onClick={props.onClose} />
          </CardHeaderRight>
        </CardHeader>
        <CardBody>
          {loading && <LinearLoading />}
          <FormContainer style={{ alignItems: 'flex-start' }}>
            <ControlSpacer style={{ width: '100%' }}>
              <ControlLabel>Export Type</ControlLabel>
              <Select
                options={averageOptions}
                value={selectedOption}
                isDisabled={isDisabled || loading}
                onChange={(e: ISelectOption): void => {
                  setSelectedOption(e);
                }}
              />
            </ControlSpacer>
            <ControlSpacer>
              <SelectBox
                label='Include Inactive Metrics'
                selected={includeInactiveMetrics}
                onSelect={handleInactiveMetricsSelection}
              />
            </ControlSpacer>
            <ControlSpacer>
              <SelectBox
                label='Deidentify Data'
                selected={deidentify}
                onSelect={handleDeidentificationOption}
              />
            </ControlSpacer>
            <ControlSpacer>
              <SelectBox
                label='Use Decimal Comma (EUR)'
                selected={useDecimalComma}
                onSelect={handleUseDecimalComma}
              />
            </ControlSpacer>
            <ControlSpacer>
              <ExportDateFormat
                onChange={handleExportDateFormatSelection}
                value={dateFormat}
              />
            </ControlSpacer>
          </FormContainer>
        </CardBody>
        <CardFooter surface={8}>
          <CardHeaderRight>
            <Button
              onClick={download}
              type='submit'
              disabled={isDisabled || loading}
              icon='cloud_download'
              title='Export Tests'
              style={{ whiteSpace: 'nowrap' }}
            />
          </CardHeaderRight>
        </CardFooter>
      </PopupCardContent>
    </PopupCard>
  );
};

interface IProps extends RouteComponentProps {
  entity: Entity;
  entityType: EntityType;
  range?: IRange;
  facet?: string;
  disabled?: boolean;
}

export const ExportPopup = withRouter((props: IProps): React.ReactElement => {
  const open = (): void => {
    openPopup(
      <Popup
        onClose={closePopup(props.history)}
        entity={props.entity}
        entityType={props.entityType}
        range={props.range}
        facet={props.facet}
      />,
      props.history,
      'export-tests'
    );
  };
  return (
    <Button onClick={open} icon='cloud_download' title='Export Tests' disabled={props.disabled} style={{ whiteSpace: 'nowrap' }} />
  );
});
